/**
 * Interactive Client Logic for Services Single Page
 * Handles Stacking Cards animation, Timeline Fill, Lead Submission & Smooth Navigation
 */

export function mountServicesPage(root = document) {
	const page = root.querySelector("[data-services-page]");
	if (!page) return;

	initSmoothScroll(page);
	initCardStacking(page);
	initProcessTimeline(page);
	initServiceForm(page);
	initFaqAccordion(page);
}

function initSmoothScroll(page) {
	const scrollLinks = page.querySelectorAll("[data-scroll]");
	scrollLinks.forEach((link) => {
		link.addEventListener("click", (event) => {
			const targetId = link.getAttribute("href");
			if (!targetId || !targetId.startsWith("#")) return;

			const targetElement = page.querySelector(targetId);
			if (!targetElement) return;

			event.preventDefault();

			// If link specified a service to preselect in the contact form
			const serviceChoice = link.dataset.serviceSelect;
			if (serviceChoice) {
				const serviceDropdown = page.querySelector("#f-service");
				if (serviceDropdown) {
					serviceDropdown.value = serviceChoice;
					serviceDropdown.dispatchEvent(new Event("change", { bubbles: true }));
				}
			}

			targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
			history.replaceState(null, "", targetId);
		});
	});
}

function initCardStacking(page) {
	const wraps = [...page.querySelectorAll(".service-stack-card-wrap")];
	const cards = wraps.map((w) => w.querySelector(".service-stack-card"));
	if (!wraps.length) return;

	const mq = window.matchMedia("(min-width: 821px)");
	const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	function updateStack() {
		if (!mq.matches || prefersReduced) {
			cards.forEach((c) => {
				if (c) {
					c.style.transform = "";
					c.style.filter = "";
				}
			});
			return;
		}

		wraps.forEach((wrap, i) => {
			const card = cards[i];
			const nextWrap = wraps[i + 1];
			if (!card || !nextWrap) return;

			const top = nextWrap.getBoundingClientRect().top;
			const stick = 90 + (i + 1) * 16;
			const p = Math.min(1, Math.max(0, 1 - (top - stick) / (window.innerHeight * 0.55)));

			card.style.transform = `scale(${(1 - p * 0.04).toFixed(4)})`;
			card.style.filter = p > 0 ? `brightness(${(1 - p * 0.12).toFixed(3)})` : "";
		});
	}

	window.addEventListener("scroll", updateStack, { passive: true });
	window.addEventListener("resize", updateStack, { passive: true });
	updateStack();
}

function initProcessTimeline(page) {
	const stepsWrap = page.querySelector(".service-steps-wrapper");
	const fillLine = page.querySelector("[data-process-fill]");
	const stepItems = [...page.querySelectorAll(".service-step-item")];
	if (!stepsWrap || !fillLine) return;

	function updateSteps() {
		const rect = stepsWrap.getBoundingClientRect();
		const mid = window.innerHeight * 0.55;
		const progress = Math.min(1, Math.max(0, (mid - rect.top) / rect.height));

		fillLine.style.transform = `scaleY(${progress.toFixed(3)})`;

		stepItems.forEach((item) => {
			const itemTop = item.getBoundingClientRect().top;
			item.classList.toggle("is-active", itemTop < mid);
		});
	}

	window.addEventListener("scroll", updateSteps, { passive: true });
	updateSteps();
}

function initFaqAccordion(page) {
	const details = page.querySelectorAll(".service-faq-accordion details");
	details.forEach((item) => {
		item.addEventListener("toggle", () => {
			if (item.open) {
				details.forEach((other) => {
					if (other !== item) other.open = false;
				});
			}
		});
	});
}

function initServiceForm(page) {
	const form = page.querySelector("[data-service-form]");
	if (!form) return;

	const status = form.querySelector("#serviceFormStatus");
	const submitBtn = form.querySelector("#serviceFormSubmit");

	form.addEventListener("submit", async (event) => {
		event.preventDefault();

		// Honeypot check
		if (form.company && form.company.value) return;

		let hasError = false;
		const requiredFields = ["name", "email", "service", "message"];

		requiredFields.forEach((fieldName) => {
			const field = form.elements[fieldName];
			const wrapper = field?.closest(".form-field");
			if (!field) return;

			let isValid = Boolean(field.value.trim());
			if (fieldName === "email" && isValid) {
				isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim());
			}

			if (wrapper) {
				wrapper.classList.toggle("has-error", !isValid);
			}

			if (!isValid && !hasError) {
				hasError = true;
				field.focus();
			}
		});

		if (hasError) {
			if (status) {
				status.className = "service-form-status is-error";
				status.textContent = "Please fill in all required fields marked with *";
			}
			return;
		}

		if (status) {
			status.className = "service-form-status";
			status.textContent = "Sending your consultation request…";
		}
		if (submitBtn) {
			submitBtn.disabled = true;
			submitBtn.textContent = "Submitting…";
		}

		try {
			const formData = new FormData(form);
			formData.append("page", "/services");
			formData.append("full_name", formData.get("name") || "");

			const rawToken = window.frappe?.csrf_token;
			const csrfToken = (rawToken && rawToken !== "None" && rawToken !== "null") ? rawToken : "";
			const headers = csrfToken ? { "X-Frappe-CSRF-Token": csrfToken } : {};

			const response = await fetch("/api/method/solufy_website.api.leads.create_lead", {
				method: "POST",
				headers,
				body: formData,
				credentials: "same-origin",
			});

			const result = await response.json();
			if (!response.ok || result.exc) {
				const errMsg = result._server_messages
					? JSON.parse(result._server_messages).map((m) => JSON.parse(m).message).join(" ")
					: (result.message || "Unable to send request. Please try again.");
				throw new Error(errMsg);
			}

			document.dispatchEvent(new CustomEvent("solufy:form-result", { detail: { form, success: true } }));
					form.reset();
			if (status) {
				status.className = "service-form-status is-success";
				status.textContent = "Thank you! Your consultation request has been received. Our ERP architect will get in touch shortly.";
			}
			if (submitBtn) {
				submitBtn.textContent = "Request Sent ✓";
				setTimeout(() => {
					submitBtn.disabled = false;
					submitBtn.textContent = "Request a free consultation →";
				}, 6000);
			}
		} catch (error) {
				 document.dispatchEvent(new CustomEvent("solufy:form-result", { detail: { form, success: false } }));
			if (status) {
				status.className = "service-form-status is-error";
				status.textContent = error.message || "Failed to send request. Please call +1 (848) 248-0021.";
			}
			if (submitBtn) {
				submitBtn.disabled = false;
				submitBtn.textContent = "Request a free consultation →";
			}
		}
	});

	// Clear errors on input
	form.addEventListener("input", (event) => {
		const wrapper = event.target.closest(".form-field");
		if (wrapper && wrapper.classList.contains("has-error")) {
			wrapper.classList.remove("has-error");
		}
	});
}
